import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Cpu, ShieldCheck, Loader2, RefreshCw, AlertTriangle, Zap, Battery, Sun, Car, Thermometer, Home } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONTROL_BADGE = {
  control_blocked: { label: 'BLOCKED', cls: 'bg-red-100 text-red-700 border-red-200' },
  control_not_supported: { label: 'NOT SUPPORTED', cls: 'bg-gray-100 text-gray-600 border-gray-200' },
  control_research_only: { label: 'RESEARCH ONLY', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  control_simulated_only: { label: 'SIMULATED ONLY', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  control_possible_later_requires_go: { label: 'GO REQUIRED', cls: 'bg-purple-50 text-purple-700 border-purple-200' },
};

const SEVERITY_BADGE = {
  high: 'bg-red-50 text-red-700 border-red-200',
  medium: 'bg-amber-50 text-amber-700 border-amber-200',
  low: 'bg-blue-50 text-blue-700 border-blue-200',
  info: 'bg-gray-50 text-gray-700 border-gray-200',
};

const CATEGORY_ICON = {
  A_p1_meter: Zap, B_smart_plugs: Zap, C_lighting: Zap,
  D_inverters: Sun, E_batteries: Battery, F_ev: Car,
  G_climate: Thermometer, H_boiler: Thermometer,
  I_appliances: Home, J_ventilation: Home, K_window_coverings: Home,
  L_sensors: Cpu, Z_security_medical_blocked: ShieldCheck, _unknown: AlertTriangle,
};

function DeviceCard({ d }) {
  const Icon = CATEGORY_ICON[d.powersure_category] || Cpu;
  const badge = CONTROL_BADGE[d.control_status] || CONTROL_BADGE.control_blocked;
  const dupBadge = {
    primary:   { label: 'PRIMARY',  cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    duplicate: { label: 'DUPLICATE', cls: 'bg-orange-50 text-orange-700 border-orange-200' },
    submeter:  { label: 'SUBMETER',  cls: 'bg-sky-50 text-sky-700 border-sky-200' },
    unknown:   { label: 'UNKNOWN',   cls: 'bg-gray-100 text-gray-600 border-gray-200' },
  }[d.duplicate_status] || null;
  return (
    <div
      data-testid={`sim-device-${d.key}`}
      className="border border-border rounded-xl p-4 bg-white hover:shadow-md transition"
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Icon className="w-5 h-5 text-primary shrink-0" />
          <div className="min-w-0">
            <div className="font-semibold truncate">{d.name}</div>
            <div className="text-xs text-muted-foreground truncate">
              {d.homey_class || '—'}{d.homey_virtual_class ? ` · ${d.homey_virtual_class}` : ''}
            </div>
          </div>
        </div>
        <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold whitespace-nowrap ${badge.cls}`}
              data-testid={`sim-device-${d.key}-control-status`}>
          {badge.label}
        </span>
      </div>

      <div className="text-xs space-y-1 mb-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-muted-foreground">Categorie:</span>
          <span className="font-mono">{d.powersure_category}</span>
          {d.source_type && (
            <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200">
              {d.source_type}
            </span>
          )}
          {dupBadge && (
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full border font-semibold ${dupBadge.cls}`}
                  data-testid={`sim-device-${d.key}-dup-status`}>
              {dupBadge.label}
            </span>
          )}
        </div>
        <div><span className="text-muted-foreground">Safety:</span> {d.safety_class}{d.installer_required && ' · installer-required'}</div>
        <div className="flex flex-wrap gap-x-3 gap-y-1">
          {typeof d.measure_power_w === 'number' && (
            <span data-testid={`sim-device-${d.key}-power`}><strong>{d.measure_power_w}</strong> W</span>
          )}
          {typeof d.meter_power_kwh === 'number' && (
            <span data-testid={`sim-device-${d.key}-energy`}><strong>{d.meter_power_kwh}</strong> kWh</span>
          )}
          {typeof d.grid_import_kwh === 'number' && (
            <span>import <strong>{d.grid_import_kwh}</strong> kWh</span>
          )}
          {typeof d.grid_export_kwh === 'number' && (
            <span>export <strong>{d.grid_export_kwh}</strong> kWh</span>
          )}
          {typeof d.meter_gas_m3 === 'number' && (
            <span>gas <strong>{d.meter_gas_m3}</strong> m³</span>
          )}
          {typeof d.measure_temperature_c === 'number' && (
            <span>{d.measure_temperature_c}°C</span>
          )}
          {typeof d.measure_battery_pct === 'number' && (
            <span>SOC {d.measure_battery_pct}%</span>
          )}
          {typeof d.target_power_w === 'number' && (
            <span>target {d.target_power_w} W</span>
          )}
          {d.onoff !== undefined && d.onoff !== null && (
            <span>onoff: {String(d.onoff)}</span>
          )}
          {d.charging !== undefined && d.charging !== null && (
            <span>charging: {String(d.charging)}</span>
          )}
        </div>
        {Array.isArray(d.serves_metrics) && d.serves_metrics.length > 0 && (
          <div className="text-[10px] text-muted-foreground">
            serves: {d.serves_metrics.join(', ')}
          </div>
        )}
      </div>

      {Array.isArray(d.capabilities) && d.capabilities.length > 0 && typeof d.capabilities[0] === 'object' && (
        <details className="text-xs">
          <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
            Capabilities ({d.capabilities.length})
          </summary>
          <div className="mt-2 grid gap-1">
            {d.capabilities.map((c) => (
              <div key={c.key} className="flex items-center justify-between font-mono text-[11px]">
                <span className="truncate">{c.key}</span>
                <span className="flex gap-1">
                  {c.readable && <span className="text-blue-600">R</span>}
                  {c.writable && <span className="text-amber-600">W</span>}
                  {c.energy_relevant && <span className="text-green-600">⚡</span>}
                  <span className={`px-1 rounded text-[10px] ${
                    c.control_risk_level === 'blocked' ? 'bg-red-100 text-red-700' :
                    c.control_risk_level === 'high' ? 'bg-orange-100 text-orange-700' :
                    c.control_risk_level === 'medium' ? 'bg-amber-100 text-amber-700' :
                    c.control_risk_level === 'low' ? 'bg-green-100 text-green-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>{c.control_risk_level}</span>
                </span>
              </div>
            ))}
          </div>
        </details>
      )}

      {d.advice && d.advice.length > 0 && (
        <div className="mt-3 pt-3 border-t border-border space-y-1">
          {d.advice.map((a, i) => (
            <div key={i} className={`text-[11px] border rounded px-2 py-1 ${SEVERITY_BADGE[a.severity] || SEVERITY_BADGE.info}`}
                 data-testid={`sim-device-${d.key}-advice-${i}`}>
              <strong className="uppercase mr-1">{a.topic}</strong>
              <span>{a.message || a.explanation}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AdviceCard({ a, testIdPrefix }) {
  const sev = SEVERITY_BADGE[a.severity] || SEVERITY_BADGE.info;
  return (
    <div className={`text-xs border rounded-lg p-3 ${sev}`} data-testid={`${testIdPrefix}-${a.topic}`}>
      <div className="font-semibold mb-1 flex items-center gap-2">
        <span className="uppercase text-[10px] tracking-wide">{a.topic}</span>
        <span>{a.title || ''}</span>
      </div>
      <div className="mb-1">{a.explanation || a.message}</div>
      {a.evidence_source && (
        <div className="text-[10px] opacity-80">evidence: {a.evidence_source}</div>
      )}
      {a.next_best_action && (
        <div className="text-[11px] mt-1"><strong>→</strong> {a.next_best_action}</div>
      )}
      {a.blocked_warning && (
        <div className="text-[11px] mt-1 text-red-800">⚠ {a.blocked_warning}</div>
      )}
      {a.claim_safety && (
        <div className="text-[10px] opacity-70 mt-1 italic">{a.claim_safety}</div>
      )}
    </div>
  );
}

function ScenarioBlock({ s }) {
  return (
    <div
      data-testid={`sim-scenario-${s.key}`}
      className="border border-border rounded-xl p-4 bg-white"
    >
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-base" data-testid={`sim-scenario-${s.key}-title`}>{s.title}</h3>
        <span className="text-xs text-muted-foreground">
          {s.device_count} devices · {s.energy_relevant_count} energie-relevant
        </span>
      </div>
      <p className="text-xs text-muted-foreground mb-3">{s.description}</p>

      {s.device_count === 0 && (
        <div className="border border-dashed border-border rounded p-3 text-xs text-muted-foreground"
             data-testid={`sim-scenario-${s.key}-empty`}>
          ✓ Homey gekoppeld, maar er zijn nog geen apparaten verbonden. Powersure gebruikt
          de capability catalog om toekomstige apparaten te herkennen.
        </div>
      )}

      {s.device_count > 0 && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3 text-xs">
            <div className="bg-muted rounded p-2">
              <div className="text-muted-foreground">Totaal vermogen (primary)</div>
              <div className="font-semibold">{s.total_power_w_primary_only} W</div>
            </div>
            <div className="bg-muted rounded p-2">
              <div className="text-muted-foreground">Totaal energie (primary)</div>
              <div className="font-semibold">{s.total_energy_kwh_primary_only} kWh</div>
            </div>
            <div className="bg-muted rounded p-2">
              <div className="text-muted-foreground">Source types</div>
              <div className="font-semibold">{Object.keys(s.by_source_type || {}).length}</div>
            </div>
            <div className="bg-muted rounded p-2">
              <div className="text-muted-foreground">Duplicate status</div>
              <div className="font-semibold text-[11px]">
                {Object.entries(s.by_duplicate_status || {}).map(([k, v]) => `${k}:${v}`).join(' · ')}
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {(s.devices || []).map((d) => (
              <DeviceCard key={d.key} d={d} />
            ))}
          </div>
        </>
      )}

      {s.aggregate_advice && s.aggregate_advice.length > 0 && (
        <div className="mt-3 space-y-2">
          {s.aggregate_advice.map((a, i) => (
            <AdviceCard key={i} a={a} testIdPrefix={`sim-scenario-${s.key}-advice`} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function HomeySimulationTab({ token }) {
  const [devices, setDevices] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('devices'); // devices | scenarios | sandbox

  // Sandbox state
  const [sbDevice, setSbDevice] = useState('smart_plug_with_energy');
  const [sbCap, setSbCap] = useState('onoff');
  const [sbValue, setSbValue] = useState('true');
  const [sbConsent, setSbConsent] = useState(false);
  const [sbInstaller, setSbInstaller] = useState(false);
  const [sbWhitelist, setSbWhitelist] = useState(false);
  const [sbGo, setSbGo] = useState(false);
  const [sbResult, setSbResult] = useState(null);
  const [sbBusy, setSbBusy] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, s, sum] = await Promise.all([
        axios.get(`${API}/admin/homey-sim/devices`, { headers }),
        axios.get(`${API}/admin/homey-sim/scenarios`, { headers }),
        axios.get(`${API}/admin/homey-sim/advice-summary`, { headers }),
      ]);
      setDevices(d.data?.devices || []);
      setScenarios(s.data?.scenarios || []);
      setSummary(sum.data?.summary || null);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Laden mislukt';
      toast.error(`Homey simulatie: ${msg}`);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const runSandbox = async () => {
    setSbBusy(true);
    setSbResult(null);
    try {
      let value = sbValue;
      try { value = JSON.parse(sbValue); } catch (_) { /* keep as string */ }
      const res = await axios.post(`${API}/admin/homey-sim/sandbox/action`, {
        device_key: sbDevice, capability: sbCap, value,
        has_user_consent: sbConsent,
        has_installer_signoff: sbInstaller,
        is_whitelisted: sbWhitelist,
        explicit_go: sbGo,
      }, { headers });
      setSbResult(res.data);
    } catch (e) {
      setSbResult({ error: e?.response?.data?.detail || e?.message });
    } finally {
      setSbBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="homey-simulation-tab">
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
        <ShieldCheck className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
        <div className="text-xs text-amber-900">
          <strong className="block mb-1">Read-only simulatie — geen live control.</strong>
          Deze pagina toont fake Homey + HomeWizard devices uit de capability catalog + source-priority engine.
          <span className="font-mono ml-1">control_status</span> wordt getoond voor inzicht; sturing is altijd geblokkeerd.
          Geen <code>setCapabilityValue</code>, geen Flows, geen write scopes, geen PAT/IP/MAC/serial.
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => setView('devices')}
          className={`px-3 py-1.5 text-sm rounded-lg border ${view === 'devices' ? 'bg-primary text-white border-primary' : 'bg-white border-border'}`}
          data-testid="sim-view-devices"
        >
          Devices ({devices.length})
        </button>
        <button
          onClick={() => setView('scenarios')}
          className={`px-3 py-1.5 text-sm rounded-lg border ${view === 'scenarios' ? 'bg-primary text-white border-primary' : 'bg-white border-border'}`}
          data-testid="sim-view-scenarios"
        >
          Scenarios ({scenarios.length})
        </button>
        <button
          onClick={() => setView('sandbox')}
          className={`px-3 py-1.5 text-sm rounded-lg border ${view === 'sandbox' ? 'bg-primary text-white border-primary' : 'bg-white border-border'}`}
          data-testid="sim-view-sandbox"
        >
          Sandbox
        </button>
        <button
          onClick={load}
          disabled={loading}
          className="ml-auto px-3 py-1.5 text-sm rounded-lg border border-border bg-white flex items-center gap-1"
          data-testid="sim-reload"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Reload
        </button>
      </div>

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
          <div className="bg-white border border-border rounded-lg p-3">
            <div className="text-muted-foreground">Devices</div>
            <div className="text-lg font-semibold" data-testid="sim-summary-device-count">{summary.device_count}</div>
          </div>
          <div className="bg-white border border-border rounded-lg p-3">
            <div className="text-muted-foreground">Advice topics</div>
            <div className="text-lg font-semibold">{Object.values(summary.topics || {}).reduce((a, b) => a + b, 0)}</div>
          </div>
          <div className="bg-white border border-border rounded-lg p-3">
            <div className="text-muted-foreground">High severity</div>
            <div className="text-lg font-semibold text-red-700">{summary.severities?.high || 0}</div>
          </div>
          <div className="bg-white border border-border rounded-lg p-3">
            <div className="text-muted-foreground">Live control</div>
            <div className="text-base font-semibold text-red-700" data-testid="sim-summary-control-blocked">
              {summary.control_summary?.live_control || 'BLOCKED'}
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div className="text-sm text-muted-foreground flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Laden...
        </div>
      )}

      {view === 'devices' && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="sim-devices-grid">
          {devices.map((d) => <DeviceCard key={d.key} d={d} />)}
        </div>
      )}

      {view === 'scenarios' && (
        <div className="space-y-4" data-testid="sim-scenarios-grid">
          {scenarios.map((s) => <ScenarioBlock key={s.key} s={s} />)}
        </div>
      )}

      {view === 'sandbox' && (
        <div className="border border-border rounded-xl p-4 bg-white space-y-3" data-testid="sim-sandbox-panel">
          <div className="bg-red-50 border border-red-200 rounded p-3 text-xs text-red-900">
            <strong>Sandbox = volledig in-memory.</strong> Geen echte Homey, geen echte HomeWizard,
            geen <code>setCapabilityValue</code>, geen Flows. Elke poging produceert een policy-gate trace.
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2 text-xs">
            <label className="block">
              <span className="text-muted-foreground">Device</span>
              <select value={sbDevice} onChange={(e) => setSbDevice(e.target.value)}
                      className="w-full mt-1 border border-border rounded px-2 py-1"
                      data-testid="sandbox-device">
                {devices.map((d) => <option key={d.key} value={d.key}>{d.name}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-muted-foreground">Capability</span>
              <input value={sbCap} onChange={(e) => setSbCap(e.target.value)}
                     className="w-full mt-1 border border-border rounded px-2 py-1 font-mono"
                     data-testid="sandbox-capability" />
            </label>
            <label className="block">
              <span className="text-muted-foreground">Value (JSON)</span>
              <input value={sbValue} onChange={(e) => setSbValue(e.target.value)}
                     className="w-full mt-1 border border-border rounded px-2 py-1 font-mono"
                     data-testid="sandbox-value" />
            </label>
            <div className="flex items-end">
              <button onClick={runSandbox} disabled={sbBusy}
                      className="w-full px-3 py-1.5 text-sm rounded bg-primary text-white"
                      data-testid="sandbox-run">
                {sbBusy ? 'Running...' : 'Run simulated action'}
              </button>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 text-xs">
            <label className="flex items-center gap-1"><input type="checkbox" checked={sbConsent} onChange={(e) => setSbConsent(e.target.checked)} data-testid="sandbox-consent" /> user consent</label>
            <label className="flex items-center gap-1"><input type="checkbox" checked={sbInstaller} onChange={(e) => setSbInstaller(e.target.checked)} data-testid="sandbox-installer" /> installer signoff</label>
            <label className="flex items-center gap-1"><input type="checkbox" checked={sbWhitelist} onChange={(e) => setSbWhitelist(e.target.checked)} data-testid="sandbox-whitelist" /> device whitelist</label>
            <label className="flex items-center gap-1"><input type="checkbox" checked={sbGo} onChange={(e) => setSbGo(e.target.checked)} data-testid="sandbox-go" /> <strong>explicit GO</strong></label>
          </div>

          {sbResult && (
            <div className="border border-border rounded p-3 text-xs space-y-2" data-testid="sandbox-result">
              <div className="flex items-center gap-2">
                <span className="font-semibold">Result:</span>
                <span className={`px-2 py-0.5 rounded-full border font-semibold ${sbResult.result === 'simulated_ok' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                  {sbResult.result || sbResult.error || 'no result'}
                </span>
                {sbResult.blocked_reason && (
                  <span className="font-mono">{sbResult.blocked_reason}</span>
                )}
              </div>
              {sbResult.control_summary && (
                <div className="text-[11px] text-muted-foreground">
                  live_call_made: <strong className="text-red-700">{String(sbResult.control_summary.live_call_made)}</strong>
                  {' · '}simulation: {String(sbResult.control_summary.this_is_a_simulation)}
                </div>
              )}
              {Array.isArray(sbResult.gates) && (
                <div className="space-y-1">
                  <div className="font-semibold">Policy gates:</div>
                  {sbResult.gates.map((g, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className={`w-4 text-center ${g.passed ? 'text-green-700' : 'text-red-700'}`}>{g.passed ? '✓' : '✗'}</span>
                      <span className="font-mono">{g.gate}</span>
                      <span className="text-muted-foreground">{typeof g.info === 'object' ? JSON.stringify(g.info) : String(g.info)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
